/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Settings → Raspberry Pi display.
 *
 * Generate a pairing code, see which screens are paired, revoke one.
 *
 * ── THE ONE THING THIS SCREEN HAS TO GET RIGHT ──────────────────────────────
 *
 * A display token is long-lived and read-only, and it lives unencrypted on a
 * device in a shared room. That is an acceptable trade for a screen on a wall
 * ONLY IF revoking is obvious and immediate. So this card leads with the list
 * of paired displays and their last-seen times, not with the generate button —
 * "which screens can read our meal plan, and when did each last ask" is the
 * question worth answering here, and a display last seen four months ago is
 * the one worth revoking.
 *
 * The code is shown exactly once, because that is all the server has: only a
 * SHA-256 hash is stored, so there is no "show it again". The copy says so
 * rather than leaving people hunting for it later.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DisplaysApi } from "../../api/endpoints.ts";
import type { DisplayDevice } from "../../api/types.ts";
import { Loading, Spinner } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";

/** "3 minutes ago" / "yesterday" / "12 Aug" — never a raw timestamp. */
function lastSeen(iso: string | null): string {
  if (!iso) return "never connected";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "never connected";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 8) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" })
    .format(new Date(then));
}

/**
 * A display polls every five minutes, so anything past ~20 is not merely idle —
 * it is unplugged, off the network, or in a drawer. Worth flagging, because a
 * screen you have forgotten about is exactly the one still holding a token.
 */
function isQuiet(d: DisplayDevice): boolean {
  if (d.revokedAt) return false;
  if (!d.lastSeenAt) return true;
  return Date.now() - new Date(d.lastSeenAt).getTime() > 20 * 60_000;
}

export function DisplaysCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState<string | null>(null);

  const displays = useQuery({
    queryKey: ["displays"],
    queryFn: () => DisplaysApi.list(),
  });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ["displays"] }); };
  const onErr = (e: Error) => toast(e.message, "danger");

  const generate = useMutation({
    mutationFn: () => DisplaysApi.createCode(),
    onSuccess: (data) => { setCode(data.code); refresh(); },
    onError: onErr,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => DisplaysApi.revoke(id),
    onSuccess: () => { refresh(); toast("Display revoked.", "ok"); },
    onError: onErr,
  });

  const cancelCode = useMutation({
    mutationFn: (id: string) => DisplaysApi.cancelCode(id),
    onSuccess: () => { setCode(null); refresh(); toast("Code cancelled.", "ok"); },
    onError: onErr,
  });

  const devices = displays.data?.devices ?? [];
  const pending = displays.data?.pendingCodes ?? [];
  const live = devices.filter((d) => !d.revokedAt);
  const revoked = devices.filter((d) => d.revokedAt);

  return (
    <section className="card mb-6 p-5">
      <h2 className="font-medium">Raspberry Pi display</h2>
      <p className="mb-3 text-sm text-ink-3">
        A kitchen screen showing today's meals. It can read the plan and nothing
        else — it can't change anything, and you can revoke it here at any time.
      </p>

      {displays.isLoading ? <Loading /> : (
        <>
          {live.length === 0 && pending.length === 0 && (
            <p className="text-sm text-ink-3">No displays paired yet.</p>
          )}

          {live.length > 0 && (
            <ul className="divide-y divide-rule-soft">
              {live.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{d.label}</span>
                    <span className="text-xs text-ink-3">
                      {/* The hint is the last four characters of the token —
                          enough to tell two screens apart when a family gave
                          them the same name, and half the entropy short of
                          being useful to anyone. */}
                      <span className="font-mono">···{d.hint}</span>
                      {" · "}
                      <span className={isQuiet(d) ? "text-warn" : undefined}>
                        {lastSeen(d.lastSeenAt)}
                      </span>
                    </span>
                  </span>
                  <button className="btn-danger shrink-0 px-2 py-1 text-xs"
                          disabled={revoke.isPending}
                          onClick={() => revoke.mutate(d.id)}>
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ── Generate ────────────────────────────────────────────────── */}
          <div className="mt-4 border-t border-rule-soft pt-4">
            <button className="btn-primary" disabled={generate.isPending}
                    onClick={() => generate.mutate()}>
              {generate.isPending ? <Spinner className="h-4 w-4" /> : "Generate device code"}
            </button>

            {code && (
              <div className="mt-3 rounded-lg border border-rule-soft bg-paper p-3 text-center">
                <div className="font-mono text-lg tracking-[0.2em] text-ink">{code}</div>
                <button className="mt-1 text-xs text-accent hover:underline"
                        onClick={() => {
                          void navigator.clipboard.writeText(code);
                          toast("Copied. It expires in 15 minutes.", "ok");
                        }}>
                  Copy — expires in 15 minutes
                </button>
                <p className="mt-2 text-xs text-ink-3">
                  On the Pi, run <span className="font-mono">mealras pair</span> and
                  type this. It works once, and we can't show it to you again.
                </p>
              </div>
            )}

            {/* Outstanding codes are live ways into the plan, so they are shown
                even after you navigate away and come back — and cancellable. */}
            {pending.length > 0 && (
              <ul className="mt-3 space-y-1">
                {pending.map((c) => (
                  <li key={c.id}
                      className="flex items-center justify-between gap-3 text-xs text-ink-3">
                    <span>
                      Unused code <span className="font-mono">···{c.hint}</span>
                      {" · expires "}
                      {new Intl.DateTimeFormat(undefined, {
                        hour: "2-digit", minute: "2-digit",
                      }).format(new Date(c.expiresAt))}
                    </span>
                    <button className="shrink-0 px-1.5 text-ink-3 hover:text-danger"
                            title="Cancel this code"
                            disabled={cancelCode.isPending}
                            onClick={() => cancelCode.mutate(c.id)}>✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {revoked.length > 0 && (
            <details className="mt-4 border-t border-rule-soft pt-3">
              {/* Kept rather than deleted: a display that vanishes from the list
                  leaves you unsure whether you revoked it or never paired it. */}
              <summary className="cursor-pointer text-xs text-ink-3">
                {revoked.length} revoked display{revoked.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-ink-4">
                {revoked.map((d) => (
                  <li key={d.id}>
                    {d.label} <span className="font-mono">···{d.hint}</span>
                    {" — revoked "}
                    {new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" })
                      .format(new Date(d.revokedAt!))}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
